import { CarConfigStep } from "../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { trackEvent, updateTrackingBaseLayer } from "../../../../common-deprecated/utils/tracking";
import type { AvailableInOtherGradeModalType } from "./types/SectionTypes";
import { CarConfigConfigureSection, CarConfigHighlightSection } from "./types/SectionTypes";
import { CarConfigConfigureSectionsLbx } from "./types/SectionTypesLbx";
import type { AceCtaTrackingType } from "../../../../common-deprecated/tracking/types";
import { getCarConfigSectionFromAvailableInOtherGrades } from "./availableInOtherGradesUtils";
import type { MotorizationEngineTypeType } from "../../../shared-logic/types/Motorization";

export const COMPONENT_NAME = "carconfig";
export const COMPONENT_GROUP = "new-cars";
const CAR_CONFIG_EVENT = "carconfigevent";
export const CAR_CONFIG_STEP = "carconfigstep";
const SEARCH_EVENT = "searchevent";

export const STEP_ORDER_OVERVIEW = "order-overview";
export const STEP_BUY_OVERVIEW = "buy-overview";
export const STEP_RESERVE_OVERVIEW = "reservation-overview";
export const STEP_LEASE_OVERVIEW = "leasing-overview";
export const COMPARE_PANEL = "compare-panel";
export type StepsType =
    | CarConfigStep
    | CarConfigConfigureSection
    | CarConfigConfigureSectionsLbx
    | CarConfigHighlightSection
    | typeof STEP_ORDER_OVERVIEW
    | typeof STEP_BUY_OVERVIEW
    | typeof STEP_RESERVE_OVERVIEW
    | typeof STEP_LEASE_OVERVIEW
    | typeof COMPARE_PANEL;

export const STEP_MAPPING: Record<StepsType, string> = {
    [CarConfigStep.Highlights]: "grade-overview",
    [CarConfigStep.Configure]: "",
    [CarConfigStep.Specs]: "grade-specs",

    //[carConfigSections.body]: "customize-body", // TODO: activate when needed
    [CarConfigConfigureSection.Engine]: "customize-engine",
    [CarConfigConfigureSection.Colour]: "customize-colour",
    [CarConfigConfigureSection.Wheels]: "customize-wheels",
    [CarConfigConfigureSection.Upholstery]: "customize-upholstery",
    [CarConfigConfigureSection.Packs]: "customize-options",
    // TODO: for now, the services section only contains insurance. This will need to change later
    [CarConfigConfigureSection.Services]: "customize-insurance",
    [CarConfigConfigureSection.Charging]: "customize-charging",
    [CarConfigConfigureSection.Summary]: "summary",
    [CarConfigConfigureSectionsLbx.AtmosphereWelcome]: "welcome",
    [CarConfigConfigureSectionsLbx.AtmosphereGrade]: "customize-grade",
    [CarConfigConfigureSectionsLbx.AtmosphereColor]: "customize-colour",
    [CarConfigConfigureSectionsLbx.AtmosphereUpholstery]: "customize-upholstery",
    [CarConfigConfigureSectionsLbx.AtmosphereWheels]: "customize-wheels",
    [CarConfigConfigureSectionsLbx.Powertrain]: "customize-powertrain",
    [CarConfigConfigureSectionsLbx.Packs]: "customize-options",
    [CarConfigConfigureSectionsLbx.Extras]: "customize-options",
    [CarConfigHighlightSection.SectionHeader]: "",

    "compare-panel": "compare-side-panel",
    "order-overview": "order-overview",
    "buy-overview": "buy-overview",
    "reservation-overview": "reservation-overview",
    "leasing-overview": "leasing-overview",
};

// Compare sections LBX
export enum CollapseSection {
    SectionStyling = "styling",
    SectionFeatures = "features",
    SectionPowertrains = "powertrains",
    SectionPacks = "packs",
}

// Add commitHash to the base tracking layer so this will get added to all events
export const updateTrackingLayerVersioning = (): void => {
    updateTrackingBaseLayer({
        page: {
            ...((window.tngaDataLayer?.page as Record<string, unknown>) || {}),
            versions: {
                ...(window.tngaDataLayer?.page?.versions as Record<string, unknown>),
                "aph-webcarconfig": commithash,
            },
        },
    });
};

// general: click on top navigation to open grade list
export const trackOpenGradeList = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-top",
            label: "open-grade-list",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: click to select a grade in the list
export const trackSelectGrade = (gradeName: string, ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-top",
            label: `select-grade-${gradeName}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: click on top navigation (highlights, configure, specs,...)
export const trackTopNavigationClick = (ccDestinationStep: StepsType, ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-top",
            label: STEP_MAPPING[ccDestinationStep],
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: click on cta in top navigation (buy, test drive,...)
export const trackTopNavigationClickCta = (title: string, ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-cta",
            label: title,
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: click on finance options in top navigation
// export const trackTopNavigationClickFinanceOptions = (ccCurrentStep: StepsType): void => {
//     trackEvent(
//         {
//             name: CAR_CONFIG_EVENT,
//             component: COMPONENT_NAME,
//             group: COMPONENT_GROUP,
//             action: "cc-navigation-top",
//             label: "finance-calculator",
//         },
//         null,
//         { carconfigStep: STEP_MAPPING[ccCurrentStep] },
//     );
// };

// general: click on configurator bottom navigation (engine, color,...)
export const trackBottomNavigationClick = (ccDestinationStep: StepsType, ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-bottom",
            label: STEP_MAPPING[ccDestinationStep],
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: activate 360° spin of the outside of the vehicle
export const trackActivateExterior360Spin = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "360-spin-ext",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: activate 360° spin of the inside of the vehicle
export const trackActivateInterior360Spin = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "360-spin-int",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: click on side button to save config in MyT
export const trackClickSideSaveConfig = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-side",
            label: "save-config",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: click on side button to display Toyota Code
export const trackClickSideDisplayToyotaCode = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-side",
            label: "display-configuration-code",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// general: click on side button to open quick links menu
export const trackClickSideQuickLinks = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-side",
            label: "open-quick-links-menu",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// TODO: implement later!!!
// general: click on quick links from side menu
export const trackClickQuickLinksButton = (linkTextLabel: string, ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-side",
            label: linkTextLabel,
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// Highlights
// Highlights: click on an anchor in the header menu
export const trackHighlightsHeaderMenuClick = (anchorText: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `anchor-${anchorText}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigStep.Highlights] },
    );
};

// Highlights: expand header menu (mobile only)
export const trackHighlightsMobileMenuOpen = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "expand-anchor-menu",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigStep.Highlights] },
    );
};

// TODO: implement later
// Highlights: click on a link to configure
export const trackHighlightsConfigureLink = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-cta",
            label: "carconfigurator",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigStep.Highlights] },
    );
};

// TODO: implement later
// Highlights: click on a link (other than configure)
export const trackHighlightsCtaLink = (targetURL: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: targetURL,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigStep.Highlights] },
    );
};

// Specs
// Specs: click on category to see details
export const trackSpecsCategoryClick = (specsCategory: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `view-specs-${specsCategory}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigStep.Specs] },
    );
};

// Specs: click on back to close details
export const trackSpecsCategoryClose = (specsCategory: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `close-specs-${specsCategory}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigStep.Specs] },
    );
};

// Specs: user do a search for specs (trigger when user stop typing)
export const trackSpecsSearch = (searchQuery: string, searchResults: number, specsSearchFilters: string): void => {
    trackEvent(
        {
            name: SEARCH_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-search-specs",
            label: "search",
        },
        {
            workflow: {
                name: "carconfigurator",
                step: "search-specs",
                conversion: 0,
                variant: "apheleia",
            },
            search: {
                type: "specs-search",
                query: searchQuery,
                results: searchResults,
                filters: specsSearchFilters,
            },
        },
        { carconfigStep: STEP_MAPPING[CarConfigStep.Specs] },
    );
};

// Specs: click on a search result
export const trackSpecsSearchResultClick = (
    searchQuery: string,
    searchResults: number,
    specsSearchFilters: string,
    specsCategory: string,
): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-click-search-results",
            label: `view-specs-${specsCategory}`,
        },
        {
            search: {
                type: "specs-search",
                query: searchQuery,
                results: searchResults,
                filters: specsSearchFilters,
            },
        },
        { carconfigStep: STEP_MAPPING[CarConfigStep.Specs] },
    );
};

// Specs: click to open filter list
export const trackSpecsSearchFilterListOpen = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "open-specs-filters-list",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigStep.Specs] },
    );
};

// Specs: click to apply a filter to the results
export const trackSpecsSearchFilterClick = (
    searchQuery: string,
    searchResults: number,
    specsSearchFilters: string,
): void => {
    trackEvent(
        {
            name: SEARCH_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-search-specs",
            label: "refine-search",
        },
        {
            workflow: {
                name: "carconfigurator",
                step: "refine-search-specs",
                conversion: 0,
                variant: "apheleia",
            },
            search: {
                type: "specs-search",
                query: searchQuery,
                results: searchResults,
                filters: specsSearchFilters,
            },
        },
        { carconfigStep: STEP_MAPPING[CarConfigStep.Specs] },
    );
};

// Engine

// Select engine: click to select an engine
export const trackEngineSelect = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "select-engine",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Engine] },
    );
};

// Select engine: click to open tooltip
export const trackEngineTooltip = (tooltipName: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `tooltip-${tooltipName}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Engine] },
    );
};

// Select engine: click on 'learn more' to see details
export const trackEngineMoreInfoOpen = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "view-engine-details",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Engine] },
    );
};

// Select engine: click on to close engine details overlay
export const trackEngineMoreInfoClose = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "close-engine-details",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Engine] },
    );
};

// Colors
// Select colour: click to select colour
export const trackColorSelect = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "select-colour",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Colour] },
    );
};

// Wheels

// Select wheels: click on tyre info link
export const trackWheelsTyreMoreInfoOpen = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "view-wheels-details",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Wheels] },
    );
};

// Select wheels: click to close tyre details overlay
export const trackWheelsTyreMoreInfoClose = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "close-wheels-details",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Wheels] },
    );
};

// Select wheels: click to display wheel in visualizer
export const trackVisualizerShowWheel = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "display-wheel",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Wheels] },
    );
};

// Select wheels: click to display car in visualizer
export const trackVisualizerShowCar = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "display-car",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

export const trackViewInterior = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "view-car-interior",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

export const trackViewFullScreenVisualizer = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "full-screen-visualizer",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

// Select wheels: click to add wheels in basket
export const trackWheelAddToBasket = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "select-wheels",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Wheels] },
    );
};

// Select wheels: click to add winter wheels set in basket
export const trackWinterWheelAddToBasket = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "add-winter-set",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Wheels] },
    );
};

// Select wheels: click to remove winter wheels set in basket
export const trackWinterWheelRemoveFromBasket = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "remove-winter-set",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Wheels] },
    );
};

// Select wheels: check the box to have the winter wheels fitted
// Select wheels: uncheck the box to have the winter wheels fitted
export const trackWinterWheelFitting = (fitted: boolean): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: fitted ? "add-winter-wheels-fitted" : "remove-winter-wheels-fitted",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Wheels] },
    );
};

// Upholstery

// Select wheels: click to add wheels in basket
export const trackUpholsteryAddToBasket = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "select-upholstery",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Upholstery] },
    );
};

// Options

// Select options: click on packs/extra tab
export const trackOptionsTabSwitch = (tabName: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `options-type-${tabName}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

export const trackOptionsOpenFilter = (extrasActive: boolean): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: `cc-open-filter-${extrasActive ? "extras" : "packs"}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// "Select options: user apply filter on extras", "Select options: user apply filter on packs"
export const trackOptionsApplyFilter = (extrasActive: boolean): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `filter-${extrasActive ? "extras" : "packs"}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

export const trackOpenOptionsSearch = (extrasActive: boolean): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: `cc-open-search-${extrasActive ? "extras" : "packs"}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// "Select options: user search for extras (trigger it only once when user stop
// typing)", "Select options: user search for packs (trigger it only once when user stop typing)"
export const trackOptionsSearch = (extrasActive: boolean, searchQuery: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: `cc-search-${extrasActive ? "extras" : "packs"}`,
            label: searchQuery,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: click to browse / swipe the content of a pack
export const trackOptionsPackBrowse = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "browse-pack-features",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: click on 'learn more' to see pack features
export const trackOptionsPackLearnMore = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "view-pack-features",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: click on 'learn more' to see extra features
export const trackOptionsExtrasLearnMore = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "view-extra-features",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: add a pack in basket
export const trackOptionsBasketPackAdd = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "add-pack",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: remove a pack in basket
export const trackOptionsBasketPackRemove = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "remove-pack",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: add an extra in basket
export const trackOptionsBasketExtraAdd = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "add-extra",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: remove an extra in basket
export const trackOptionsBasketExtraRemove = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "remove-extra",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: add a homecharger in basket
export const trackOptionsBasketHomeChargerAdd = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "add-homecharger",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Select options: remove a homecharger in basket
export const trackOptionsBasketHomeChargerRemove = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "remove-homecharger",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

export const trackOptionsToggleCategory = (itemCategory: string, isOpen: boolean): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `${isOpen ? "expand" : "reduce"}-${itemCategory}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Packs] },
    );
};

// Summary

// Overview: click on card to go to the related section
export const trackSummaryCardNav = (ccDestinationStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-card",
            label: STEP_MAPPING[ccDestinationStep],
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Summary] },
    );
};

// Overview: remove a pack in basket
export const trackSummaryCardRemovePack = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "remove-pack",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Summary] },
    );
};

// Overview: remove an extra in basket
export const trackSummaryCardRemoveExtra = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "remove-extra",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Summary] },
    );
};

// Overview: remove a homecharger in basket
export const trackSummaryCardRemoveHomeCharger = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "remove-homeCharger",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Summary] },
    );
};

// Order overview

// Order Summary: click on cash/finance tab
export const trackOrderOverviewTab = (tab: "cash" | "finance" | "overview" | "business" | "insurance"): void => {
    const trackingEvent = {
        cash: "cash",
        finance: "monthly",
        overview: "overview",
        business: "kinto",
        insurance: "insurance",
    };
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `change-overview-tab-${trackingEvent[tab]}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[STEP_ORDER_OVERVIEW] },
    );
};

// Order Sumary: click to open trade-in calculator
export const trackOrderOpenTradeIn = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-cta",
            label: "tradein-calculator",
        },
        null,
        { carconfigStep: STEP_MAPPING[STEP_ORDER_OVERVIEW] },
    );
};

// Order Sumary: click to open finance calculator
export const trackOrderOpenFinanceCalculator = (action: "cc-cta" | "cc-navigation-top" = "cc-cta"): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action,
            label: "finance-calculator",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Summary] },
    );
};

// Order Summary: click to activate non checkout CTA
export const trackOrderCTA = (label: AceCtaTrackingType | string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-cta",
            label,
        },
        null,
        { carconfigStep: STEP_MAPPING[STEP_ORDER_OVERVIEW] },
    );
};

// Order Sumary: click to expand a section in specs
export const trackOrderOverviewSpecsPanelOpen = (
    expand: boolean,
    tabName: "tech-specs" | "interior" | "exterior" | "eco-specs",
): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `${expand ? "expand" : "reduce"}-${tabName}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Summary] },
    );
};

// Order Summary: click on tooltip
export const trackOrderOverviewTooltip = (tooltipName: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `tooltip-${tooltipName}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[STEP_ORDER_OVERVIEW] },
    );
};

// Order Summary: click to see finance terms
export const trackOrderOverviewFinanceTerms = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "view-finance-terms",
        },
        null,
        { carconfigStep: STEP_MAPPING[STEP_ORDER_OVERVIEW] },
    );
};

// Insurance
// Insurance: click on button/link to open insurance calculator (get a quote or edit)
export const trackOpenInsuranceCalculator = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-cta",
            label: "insurance-calculator",
        },
        null,
        // TODO: change later
        { carconfigStep: STEP_MAPPING[STEP_ORDER_OVERVIEW] },
    );
};

// Insurance: click on button to remove insurance from basket
export const trackRemoveInsurance = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "remove-insurance",
        },
        null,
        // TODO: change later
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSection.Services] },
    );
};

// Order Summary: click to see insurance terms
export const trackOrderOverviewInsuranceInfo = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "insurance-more-infos",
        },
        null,
        { carconfigStep: STEP_MAPPING[STEP_ORDER_OVERVIEW] },
    );
};

/**
 * Custom LBX tracking
 */
// Select a grade in the atmosphere screen
export const trackSelectAtmosphereGrade = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "select-grade",
        },
        null,
        { carconfigStep: STEP_MAPPING[CarConfigConfigureSectionsLbx.AtmosphereGrade] },
    );
};

// Browse through the grades
export const trackBrowseAtmosphereGrades = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "browse-grade",
        },
        null,
        {
            carconfigStep: STEP_MAPPING[CarConfigConfigureSectionsLbx.AtmosphereGrade],
        },
    );
};

// Track clicking on the view packs options button
export const trackViewPackOptions = (gradeName: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `view-options-${gradeName}`,
        },
        null,
        {
            carconfigStep: STEP_MAPPING[CarConfigConfigureSectionsLbx.AtmosphereGrade],
        },
    );
};

// Track the button that scrolls to the atmosphere screen from the wheels
export const trackViewAtmosphereWheels = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "view-other-grades-wheels",
        },
        null,
        {
            carconfigStep: STEP_MAPPING[CarConfigConfigureSectionsLbx.AtmosphereWheels],
        },
    );
};

// Track the button to open the compare on the packs screen
export const trackViewPackFeatures = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "view-pack-features",
        },
        null,
        {
            carconfigStep: STEP_MAPPING[CarConfigConfigureSectionsLbx.Packs],
        },
    );
};

export const trackPowertrainLearnMore = (engineType: MotorizationEngineTypeType): void => {
    const getEngineType = (): "HEV" | "BEV" | "FCEV" | "PHEV" => {
        if (engineType.hybrid) {
            return "HEV";
        } else if (engineType.electric) {
            return "BEV";
        } else if (engineType.fuelCell) {
            return "FCEV";
        } else if (engineType.plugin) {
            return "PHEV";
        } else {
            return "HEV";
        }
    };

    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `view-details-${getEngineType()}`,
        },
        null,
        {
            carconfigStep: STEP_MAPPING[CarConfigConfigureSectionsLbx.Powertrain],
        },
    );
};

// Track selecting a powertrain
export const trackPowertrainAddToBasket = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-customize",
            label: "select-powertrain",
        },
        null,
        {
            carconfigStep: STEP_MAPPING[CarConfigConfigureSectionsLbx.Powertrain],
        },
    );
};

/**
 * Compare tracking
 */

// Add a grade to compare
export const trackGradeCompare = (gradeName: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `add-grade-${gradeName}`,
        },
        null,
        {
            carconfigstep: STEP_MAPPING["compare-panel"],
        },
    );
};

// Close sidepanel
export const trackClosingSidepanel = (): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: "close-panel",
        },
        null,
        {
            carconfigstep: STEP_MAPPING["compare-panel"],
        },
    );
};

// Expand/collapse all items
export const trackExpandeditems = (expanded: boolean): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `${!expanded ? "expand" : "reduce"}-all-sections`,
        },
        null,
        {
            carconfigstep: STEP_MAPPING["compare-panel"],
        },
    );
};

// Expand/Collapse 1 item
export const trackExpandedItem = (expanded: boolean, sectionName: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `${!expanded ? "expand" : "reduce"}-${sectionName}`,
        },
        null,
        {
            carconfigstep: STEP_MAPPING["compare-panel"],
        },
    );
};

export const trackViewAllCompare = (sectionName: string): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `view-all-${sectionName}`,
        },
        null,
        {
            carconfigstep: STEP_MAPPING["compare-panel"],
        },
    );
};

export const trackCtaCheckout = (
    label:
        | "online-retailing-checkout"
        | "online-retailing-checkout-buy"
        | "online-retailing-checkout-reservation"
        | "online-retailing-checkout-leasing",
    step: StepsType,
): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-cta",
            label,
        },
        null,
        { carconfigStep: STEP_MAPPING[step] },
    );
};

// Available in other grades

// Available in other grades: select item (open modal)
export const trackAvailableInOtherGradesSelectItem = (
    availableInOtherGradesSection: AvailableInOtherGradeModalType,
): void => {
    const section = getCarConfigSectionFromAvailableInOtherGrades(availableInOtherGradesSection);
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-action",
            label: `view-other-grades-${section.sectionString}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[section.carConfigSection] },
    );
};

// Available in other grades: go back to current grade
export const trackAvailableInOtherGradesBackToCurrentGrade = (
    gradeName: string,
    availableInOtherGradesSection: AvailableInOtherGradeModalType,
): void => {
    const section = getCarConfigSectionFromAvailableInOtherGrades(availableInOtherGradesSection);
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: `cc-overlay-grades-${section.sectionString}`,
            label: `keep-${section.sectionString}-${gradeName}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[section.carConfigSection] },
    );
};

// Available in other grades: switch to new grade
export const trackAvailableInOtherGradesGoToNewGrade = (
    gradeName: string,
    availableInOtherGradesSection: AvailableInOtherGradeModalType,
): void => {
    const section = getCarConfigSectionFromAvailableInOtherGrades(availableInOtherGradesSection);
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: `cc-overlay-grades-${section.sectionString}`,
            label: `switch-${section.sectionString}-${gradeName}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[section.carConfigSection] },
    );
};

export const trackBackButton = (ccCurrentStep: StepsType): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-top",
            label: "view-car-details",
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};

export const trackPriceDisplayChange = (ccCurrentStep: StepsType, priceDisplay: "cash" | "monthly"): void => {
    trackEvent(
        {
            name: CAR_CONFIG_EVENT,
            component: COMPONENT_NAME,
            group: COMPONENT_GROUP,
            action: "cc-navigation-top",
            label: `change-price-display-${priceDisplay}`,
        },
        null,
        { carconfigStep: STEP_MAPPING[ccCurrentStep] },
    );
};
